import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import Hls from 'hls.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import type * as dashjs from 'dashjs';

import { useAudioPlayer, useAudioPlayerTime } from '@/shared/ui/audio-player';
import {
  courseQueries,
  postUpdateProgress,
  type Content,
} from '@/entities/course';
import { usersQueries } from '@/entities/user';
import type { ErrorResponse } from '@/shared/types/api';
import { useRouter } from '@tanstack/react-router';
import { useToast } from '../ui/toast-provider';
import { useAudioStore } from '@/shared/state/audio-store';

interface UsePlyerAudioPlayerParams {
  courseId: string;
  contentId: string;
}

const REPORT_INTERVAL_MS = 10_000;

/**
 * mimeType이 HLS 스트림인지 확인합니다.
 * @param mimeType - 미디어 타입 문자열
 * @returns HLS용인지 여부 (boolean)
 */
const isHlsSource = (mimeType: string) =>
  mimeType === 'application/x-mpegURL' ||
  mimeType === 'application/vnd.apple.mpegurl';

/**
 * mimeType이 DASH 스트림인지 확인합니다.
 * @param mimeType - 미디어 타입 문자열
 * @returns DASH용인지 여부 (boolean)
 */
const isDashSource = (mimeType: string) => mimeType === 'application/dash+xml';

/**
 * clamp 함수는 value(값)가 주어진 최소값(min)과 최대값(max) 범위 내에 있도록 제한합니다.
 * value가 min보다 작으면 min을, max보다 크면 max를 반환하며, 그 사이면 value를 그대로 반환합니다.
 *
 * @param value - 제한하려는 값
 * @param min - 허용하는 최소값
 * @param max - 허용하는 최대값
 * @returns min 이상 max 이하의 값
 */
const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

type DashModule = typeof import('dashjs');
type DashModuleWithDefault = DashModule & { default?: DashModule };

export interface PlayerViewState {
  isLoading: boolean;
  isReady: boolean;
  isPlaying: boolean;
  isMuted: boolean;
  isEnded: boolean;
  isInteractionDisabled: boolean;
  currentTime: number;
  duration: number;
  progressPercent: number;
}

export interface UsePlyerAudioPlayerResult {
  state: PlayerViewState;
  courseTitle: string | undefined;
  courseDurationSecs: number;
  content: Content | undefined;
  audioRef: RefObject<HTMLAudioElement | null>;
  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  seekBy: (delta: number) => void;
  toggleMute: () => void;
  goToNextContent: () => void;
  goToPreviousContent: () => void;
}

/**
 * 오디오 플레이어 훅
 * 오디오 컨텐츠의 로딩, 재생, 일시정지, 탐색, 음소거, 진도 저장 등 주요 제어 및 상태를 제공합니다.
 *
 * @param courseId - 강의 ID
 * @param contentId - 컨텐츠 ID
 * @returns 각종 오디오 플레이어 제어 함수 및 상태 정보 반환
 */
export const usePlyerAudioPlayer = ({
  courseId,
  contentId,
}: UsePlyerAudioPlayerParams): UsePlyerAudioPlayerResult => {
  /**
   * useAudioPlayer 커스텀 훅에서 가져온 오디오 제어용 ref/메서드와 재생 시간
   */
  const {
    ref: audioRef,
    play: playInternal,
    pause: pauseInternal,
    seek: seekInternal,
    setActiveItem,
    duration: apiDuration,
    isPlaying,
  } = useAudioPlayer<Content>();
  const router = useRouter();
  const currentAudioTime = useAudioPlayerTime();
  const toast = useToast();

  const globalMuted = useAudioStore((state) => state.isMuted);
  const setGlobalMuted = useAudioStore((state) => state.setMuted);

  // HLS, DASH 인스턴스 및 리포트 관련 ref들
  const hlsRef = useRef<Hls | null>(null);
  const dashRef = useRef<dashjs.MediaPlayerClass | null>(null);
  const lastReportAtRef = useRef<number>(0);
  const latestContentRef = useRef<Content | undefined>(undefined);
  const reportProgressRef = useRef<(event: string) => void>(() => undefined);
  const courseIdRef = useRef(courseId);
  const sortedContentsRef = useRef<Content[]>([]);

  // 오디오 UI 상태값들
  const [isReady, setIsReady] = useState(false);
  const [isLoadingInternal, setIsLoadingInternal] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [isInteractionDisabled, setIsInteractionDisabled] = useState(false);
  const [durationState, setDurationState] = useState<number>(0);

  // 유저 정보(백엔드 재생 모드 여부 조회)
  const { data: user } = useQuery(usersQueries.me());
  const isServerPlaybackModeActive = user?.is_server_playback_mode_active;

  // 강좌 및 컨텐츠 데이터 요청
  const { data: course, isPending: isCoursePending } = useQuery({
    ...courseQueries.detail(courseId),
    enabled: Boolean(courseId),
  });

  /**
   * 현재 재생중인 컨텐츠(강의 내 특정 컨텐츠 검색)
   */
  const content =
    !course || !contentId
      ? undefined
      : course.contents.find((item) => item.id === contentId);

  // 최신 컨텐츠 메모(ref에 갱신)
  useEffect(() => {
    latestContentRef.current = content;
  }, [content]);

  useEffect(() => {
    courseIdRef.current = courseId;
  }, [courseId]);

  useEffect(() => {
    if (course?.contents?.length) {
      sortedContentsRef.current = course.contents;
    } else {
      sortedContentsRef.current = [];
    }
  }, [course]);

  // 기본 진도(백엔드에서 내려주는 기존 진도 값)
  const defaultProgress =
    content?.user_content_association?.progress_percent ?? 0;

  // 진도 업데이트(서버에 보내는 뮤테이션 함수 선언)
  const { mutateAsync: reportProgressMutation } = useMutation({
    mutationFn: postUpdateProgress,
  });

  /**
   * 진도(재생 위치 등) 서버로 전송
   * @param event - 조회 이벤트명 (playing/paused/timeupdate/ended 등)
   */
  const reportProgress = useCallback(
    async (event: string) => {
      if (isServerPlaybackModeActive) return;

      const audio = audioRef.current;
      if (!audio || !courseId || !contentId) return;

      const now = Date.now();
      // timeupdate 이벤트 빈도 제한 (10초)
      if (
        event === 'playing' &&
        now - lastReportAtRef.current < REPORT_INTERVAL_MS
      ) {
        return;
      }

      lastReportAtRef.current = now;

      const duration = Number.isFinite(audio.duration)
        ? audio.duration
        : durationState || 0;
      const currentTime = audio.currentTime ?? 0;

      const progressPercent =
        duration > 0
          ? clamp((currentTime / duration) * 100, 0, 100)
          : undefined;

      try {
        await reportProgressMutation({
          content_id: contentId,
          course_id: courseId,
          player_event: event,
          play_position_secs: currentTime,
          progress_percent: progressPercent,
          sound_volume: audio.volume ?? 1,
          playback_speed: audio.playbackRate ?? 1,
          is_background_playing: false,
        });
        setIsInteractionDisabled(false);
      } catch (error) {
        const err = error as ErrorResponse;
        // 400에러가 아니면 상호작용 차단
        toast({
          type: 'warning',
          title:
            err.data.errors[0]?.detail ??
            'please check with your administrator',
        });

        if (err.status !== 400) {
          setIsInteractionDisabled(true);
        }

        if (err.status === 402) {
          router.navigate({
            to: '/payment/select-course',
            search: {
              status: undefined,
              session_id: undefined,
            },
          });
          seekTo(
            (content?.user_content_association?.max_play_position_secs ?? 0) -
              1,
          );
        }
      }
    },
    [
      audioRef,
      contentId,
      courseId,
      durationState,
      isServerPlaybackModeActive,
      reportProgressMutation,
    ],
  );

  // 진도보고 함수 ref 갱신
  useEffect(() => {
    reportProgressRef.current = reportProgress;
  }, [reportProgress]);

  const findAdjacentContent = useCallback((offset: number) => {
    const activeContent = latestContentRef.current;
    if (!activeContent) return undefined;

    const orderedContents = sortedContentsRef.current;
    if (!orderedContents.length) return undefined;

    const currentIndex = orderedContents.findIndex(
      (item) => item.id === activeContent.id,
    );
    if (currentIndex === -1) return undefined;

    const targetIndex = currentIndex + offset;
    if (targetIndex < 0 || targetIndex >= orderedContents.length) {
      return undefined;
    }

    return orderedContents[targetIndex];
  }, []);

  /**
   * 오디오 엘리먼트의 각종 이벤트 핸들러 등록 및 해제
   */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = globalMuted;
    // 메타데이터 로드 완료시 (총 길이 등) 처리
    const handleLoadedMetadata = () => {
      const activeContent = latestContentRef.current;
      const duration =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : (activeContent?.duration_secs ?? 0);
      setDurationState(duration);
      setIsReady(true);
      setIsLoadingInternal(false);
      setIsMuted(audio.muted);
    };

    // 재생시 처리
    const handlePlay = () => {
      setIsLoadingInternal(false);
      setIsEnded(false);
    };

    // 일시정지시 처리
    const handlePause = () => {
      reportProgressRef.current('paused');
    };

    // 재생위치 변경·업데이트
    const handleTimeUpdate = () => {
      reportProgressRef.current('playing');
    };

    // 재생 끝남
    const handleEnded = () => {
      setIsEnded(true);
      reportProgressRef.current('ended');

      const nextContent = findAdjacentContent(1);
      const activeCourseId = courseIdRef.current;

      if (nextContent && activeCourseId) {
        if (nextContent.content_type === 'quiz') {
          router.navigate({
            to: '/courses/quiz',
            search: {
              courseId: activeCourseId,
              contentId: nextContent.id,
              quizId: nextContent.quizzes[0]?.id ?? '',
            },
          });
        }
        // 다음 콘텐츠로 이동
        router.navigate({
          to: '/courses/plyer',
          search: {
            courseId: activeCourseId,
            contentId: nextContent.id,
          },
        });
      }
    };

    // 버퍼링/로딩
    const handleWaiting = () => {
      setIsLoadingInternal(true);
    };

    const handleCanPlay = () => {
      setIsLoadingInternal(false);
    };

    // 볼륨 상태 변경
    const handleVolumeChange = () => {
      setIsMuted(audio.muted);
    };

    // 에러 발생
    const handleError = () => {
      console.error('🚀 ~ handleError ~ handleError:', audio.error);
    };

    // 이벤트 리스너 등록
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('volumechange', handleVolumeChange);
    audio.addEventListener('error', handleError);

    setIsMuted(audio.muted);

    // 언마운트시 정리
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('volumechange', handleVolumeChange);
      audio.removeEventListener('error', handleError);
    };
  }, [audioRef, findAdjacentContent, globalMuted, router]);

  /**
   * apiDuration이 내려오면 실제 duration 상태에도 반영
   */
  useEffect(() => {
    if (apiDuration && Number.isFinite(apiDuration)) {
      setDurationState(apiDuration);
    }
  }, [apiDuration]);

  // Sync audio element muted state with global store
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = globalMuted;
    setIsMuted(globalMuted);
  }, [audioRef, globalMuted]);

  /**
   * 컨텐츠 바뀌거나 활성화 아이템이 바뀔 때 오디오 소스 및 플레이어 초기화
   */
  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (!content) {
      void setActiveItem(null);
      setIsLoadingInternal(false);
      setIsReady(false);
      setIsEnded(false);
      setDurationState(0);
      return;
    }

    setIsLoadingInternal(true);
    setIsReady(false);
    setIsEnded(false);
    setIsInteractionDisabled(false);
    setDurationState(content.duration_secs ?? 0);

    const source = content.media_sources[0];

    // 기존 플레이어 정리(HLS, DASH)
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (dashRef.current) {
      dashRef.current.reset();
      dashRef.current = null;
    }

    if (!source) {
      setIsLoadingInternal(false);
      return;
    }

    let dashCancelled = false;

    const savedPlaybackSeconds = (() => {
      if (!content?.user_content_association) return 0;
      const {
        max_play_position_secs: maxPosition,
        play_position_secs: playPosition,
      } = content.user_content_association;
      return Math.max(
        0,
        typeof maxPosition === 'number' ? maxPosition : 0,
        typeof playPosition === 'number' ? playPosition : 0,
      );
    })();

    const seekRetryTimeouts: number[] = [];
    const savedPositionCleanup: Array<() => void> = [];
    let hasAppliedSavedPosition = false;

    /**
     * 저장된 재생 위치(startTime)를 계산합니다.
     * duration보다 클 경우 duration 기준으로 조정하며, 종료 직전 위치로 이동하도록 약간의 여유를 둡니다.
     */
    const resolveSavedStartTime = () => {
      if (!audio) return null;
      if (!Number.isFinite(savedPlaybackSeconds) || savedPlaybackSeconds <= 0) {
        return null;
      }

      const fallbackDuration = content.duration_secs ?? 0;
      const elementDuration =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : fallbackDuration;

      const effectiveDuration =
        elementDuration && elementDuration > 0
          ? elementDuration
          : savedPlaybackSeconds;

      if (!effectiveDuration || effectiveDuration <= 0) {
        return savedPlaybackSeconds;
      }

      const clamped = clamp(savedPlaybackSeconds, 0, effectiveDuration);
      if (clamped <= 0) {
        return null;
      }

      if (clamped >= effectiveDuration) {
        const epsilon =
          effectiveDuration > 5 ? 1 : Math.min(0.5, effectiveDuration);
        return Math.max(effectiveDuration - epsilon, 0);
      }

      return clamped;
    };

    /**
     * 이전 재생 위치로 이동 (진도 이어듣기)
     */
    const applySavedPosition = (force = false) => {
      if (!audio) return;
      const startTime = resolveSavedStartTime();
      if (startTime === null) return;

      if (!force && hasAppliedSavedPosition) {
        const delta = Math.abs(audio.currentTime - startTime);
        if (delta < 0.25) {
          return;
        }
      }

      audio.currentTime = startTime;
      hasAppliedSavedPosition = true;
    };

    /**
     * 재생 위치 적용을 즉시 수행하고 짧은 간격으로 한 번 더 재시도합니다.
     * HLS/DASH 플레이어가 소스를 재배치하면서 currentTime을 되돌리는 경우를 보완합니다.
     */
    const scheduleApplySavedPosition = (force = false) => {
      applySavedPosition(force);
      if (typeof window === 'undefined') return;

      const retryDelays = [200, 750];
      retryDelays.forEach((delay) => {
        const timeoutId = window.setTimeout(() => {
          applySavedPosition(true);
        }, delay);
        seekRetryTimeouts.push(timeoutId);
      });
    };

    /**
     * 플레이어 실제 초기화(HLS, DASH, 일반미디어)
     */
    const initialisePlayback = async () => {
      await setActiveItem({
        id: content.id,
        src: source.url,
        data: content,
      });

      if (isHlsSource(source.mime_type) && Hls.isSupported()) {
        // HLS 스트림 초기화
        const hls = new Hls();
        if (savedPlaybackSeconds > 0) {
          hls.config.startPosition = savedPlaybackSeconds;
        }
        const handleHlsManifestParsed = () => {
          scheduleApplySavedPosition(true);
          hls.off(Hls.Events.MANIFEST_PARSED, handleHlsManifestParsed);
        };
        hls.on(Hls.Events.MANIFEST_PARSED, handleHlsManifestParsed);
        savedPositionCleanup.push(() =>
          hls.off(Hls.Events.MANIFEST_PARSED, handleHlsManifestParsed),
        );
        hls.loadSource(source.url);
        hls.attachMedia(audio);
        hlsRef.current = hls;
      } else if (isDashSource(source.mime_type)) {
        // Dash.js 동적 임포트 및 초기화
        import('dashjs')
          .then((dashModule) => {
            if (dashCancelled) return;
            const runtime = dashModule as DashModuleWithDefault;
            const dashFactory = runtime.default ?? runtime;
            const dash = dashFactory.MediaPlayer().create();

            if (savedPlaybackSeconds > 0) {
              const dashEvents = dashFactory.MediaPlayer.events;
              const handleDashStreamInitialised = () => {
                scheduleApplySavedPosition(true);
                dash.off(
                  dashEvents.STREAM_INITIALIZED,
                  handleDashStreamInitialised,
                );
              };
              dash.on(
                dashEvents.STREAM_INITIALIZED,
                handleDashStreamInitialised,
              );
              savedPositionCleanup.push(() =>
                dash.off(
                  dashEvents.STREAM_INITIALIZED,
                  handleDashStreamInitialised,
                ),
              );
            }

            dash.initialize(
              audio,
              source.url,
              false,
              savedPlaybackSeconds > 0 ? savedPlaybackSeconds : undefined,
            );
            dashRef.current = dash;
          })
          .catch((error) => {
            if (dashCancelled) return;
            console.error('Failed to initialise dash.js player', error);
            setIsLoadingInternal(false);
            setIsReady(false);
          });
      } else {
        // 일반 미디어 소스
        audio.src = source.url;
        audio.load();
      }

      // saved position 미리 적용
      if (audio.readyState >= 1) {
        scheduleApplySavedPosition(true);
      } else {
        const handleMetadataForSavedPosition = () => {
          scheduleApplySavedPosition(true);
          audio.removeEventListener(
            'loadedmetadata',
            handleMetadataForSavedPosition,
          );
        };
        audio.addEventListener(
          'loadedmetadata',
          handleMetadataForSavedPosition,
        );
        savedPositionCleanup.push(() =>
          audio.removeEventListener(
            'loadedmetadata',
            handleMetadataForSavedPosition,
          ),
        );
      }

      const handleCanPlayForSavedPosition = () => {
        scheduleApplySavedPosition(true);
        audio.removeEventListener('canplay', handleCanPlayForSavedPosition);
      };
      audio.addEventListener('canplay', handleCanPlayForSavedPosition);
      savedPositionCleanup.push(() =>
        audio.removeEventListener('canplay', handleCanPlayForSavedPosition),
      );
    };

    void initialisePlayback();

    // 언마운트/컨텐츠 변경시 플레이어 정리
    return () => {
      dashCancelled = true;
      savedPositionCleanup.forEach((cleanup) => cleanup());
      if (typeof window !== 'undefined') {
        seekRetryTimeouts.forEach((timeoutId) =>
          window.clearTimeout(timeoutId),
        );
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (dashRef.current) {
        dashRef.current.reset();
        dashRef.current = null;
      }
    };
  }, [audioRef, content, setActiveItem]);

  /**
   * 기존 진도(progress)가 있고 준비중이면 해당 시간으로 이동 (진도 복원)
   */
  useEffect(() => {
    if (!isReady && defaultProgress > 0) {
      const duration = durationState || content?.duration_secs || 0;
      if (!duration) return;
      const fallbackTime = clamp(
        (defaultProgress / 100) * duration,
        0,
        duration,
      );
      const audio = audioRef.current;
      if (audio && audio.readyState < 1) {
        audio.currentTime = fallbackTime;
      }
    }
  }, [
    audioRef,
    content?.duration_secs,
    defaultProgress,
    durationState,
    isReady,
  ]);

  /**
   * 오디오 재생(플레이) 실행 함수
   */
  const play = useCallback(() => {
    if (isInteractionDisabled) return;
    playInternal().catch((error) => {
      console.error('Failed to start playback', error);
    });
  }, [isInteractionDisabled, playInternal]);

  /**
   * 오디오 일시정지 함수
   */
  const pause = useCallback(() => {
    pauseInternal();
  }, [pauseInternal]);

  /**
   * 주어진 위치(time)로 탐색(점프)
   * @param time - 이동할(초단위) 재생 위치
   */
  const seekTo = useCallback(
    (time: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (
        time > (content?.user_content_association?.max_play_position_secs ?? 0)
      ) {
        seekInternal(
          content?.user_content_association?.max_play_position_secs ?? 0,
        );
        return;
      }
      const duration =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : durationState || 0;
      const nextTime = clamp(time, 0, duration || 0);
      seekInternal(nextTime);
    },
    [audioRef, durationState, seekInternal],
  );

  /**
   * delta(초) 만큼 상대적으로 앞으로/뒤로 이동
   * @param delta - 이동할 초(음수: 뒤로, 양수: 앞으로)
   */
  const seekBy = useCallback(
    (delta: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      const nextTime = (audio.currentTime || 0) + delta;
      seekTo(nextTime);
    },
    [audioRef, seekTo],
  );

  /**
   * 음소거 토글
   */
  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const newMutedState = !audio.muted;
    audio.muted = newMutedState;
    setGlobalMuted(newMutedState);
    setIsMuted(newMutedState);
  }, [audioRef, setGlobalMuted]);

  /**
   * 다음 컨텐츠로 이동할 때 로딩 상태 처리 및 네비게이션 수행
   */
  const goToNextContent = useCallback(() => {
    const nextContent = findAdjacentContent(1);
    if (!nextContent) return;
    setIsLoadingInternal(true);
    setIsReady(false);
    setIsEnded(false);
    pauseInternal();
    const activeCourseId = courseIdRef.current;
    if (activeCourseId) {
      router.navigate({
        to: '/courses/plyer',
        search: {
          courseId: activeCourseId,
          contentId: nextContent.id,
        },
      });
    }
  }, [findAdjacentContent, pauseInternal, router]);

  /**
   * 이전 컨텐츠로 이동 (없으면 현재 컨텐츠 처음으로 이동)
   */
  const goToPreviousContent = useCallback(() => {
    const previousContent = findAdjacentContent(-1);
    if (!previousContent) {
      seekTo(0);
      return;
    }
    setIsLoadingInternal(true);
    setIsReady(false);
    setIsEnded(false);
    pauseInternal();
    const activeCourseId = courseIdRef.current;
    if (activeCourseId) {
      router.navigate({
        to: '/courses/plyer',
        search: { courseId: activeCourseId, contentId: previousContent.id },
      });
    }
  }, [findAdjacentContent, pauseInternal, router, seekTo]);

  // 실제 오디오 duration 계산
  const effectiveDuration =
    (Number.isFinite(apiDuration) && apiDuration) ||
    durationState ||
    content?.duration_secs ||
    0;

  // 현재 화면상에 보여줄 재생 위치(time)
  const displayTime = isReady
    ? currentAudioTime
    : effectiveDuration > 0
      ? clamp((defaultProgress / 100) * effectiveDuration, 0, effectiveDuration)
      : 0;

  // 현재 진도(%) 계산
  const progressPercent =
    effectiveDuration > 0
      ? clamp((displayTime / effectiveDuration) * 100, 0, 100)
      : defaultProgress;

  // PlayerViewState 구성
  const viewState: PlayerViewState = {
    isLoading: isCoursePending || isLoadingInternal,
    isReady,
    isPlaying,
    isMuted,
    isEnded,
    isInteractionDisabled,
    currentTime: displayTime,
    duration: effectiveDuration,
    progressPercent,
  };

  // 도메인 로직·상태·제어 함수들 반환
  return {
    state: viewState,
    courseTitle: course?.title,
    courseDurationSecs: course?.duration_secs ?? 0,
    content,
    audioRef,
    play,
    pause,
    seekTo,
    seekBy,
    toggleMute,
    goToNextContent,
    goToPreviousContent,
  };
};
